import React, { useState, useEffect, useMemo } from "react";
import { Bar } from "react-chartjs-2";
import AssamMap from "../AssamMap";
import { hBarOpts, sortBtn } from "../../utils/chartHelpers";
import CardHead from "../../utils/CardHead";
import {
  fetchStockoutDistricts,
  fetchStockoutFacilityType,
  fetchStockoutFacilities,
  fetchStockoutCategories,
  fetchStockoutProducts,
} from "../../data/api";

export default function TabStockout() {
  const [soDistSort, setSoDistSort] = useState("desc");
  const [soFacSort, setSoFacSort] = useState("desc");
  const [soCatSort, setSoCatSort] = useState("desc");
  const [prodSort, setProdSort] = useState("desc");
  const [soFacType, setSoFacType] = useState("desc");

  const [distData, setDistData] = useState([]);
  const [facTypeData, setFacTypeData] = useState([]);
  const [facData, setFacData] = useState([]);
  const [catData, setCatData] = useState([]);
  const [prodData, setProdData] = useState([]);

  useEffect(() => {
    fetchStockoutDistricts('desc', 10).then(r => setDistData(r.data)).catch(console.error);
    fetchStockoutFacilityType().then(r => setFacTypeData(r.data)).catch(console.error);
    fetchStockoutFacilities('asc', 10).then(r => setFacData(r.data)).catch(console.error);
    fetchStockoutCategories().then(r => setCatData(r.data)).catch(console.error);
    fetchStockoutProducts(10).then(r => setProdData(r.data)).catch(console.error);
  }, []);

  const sortedSoDist = useMemo(
    () => [...distData].sort((a, b) =>
      soDistSort === "desc" ? b.stockout_pct - a.stockout_pct : a.stockout_pct - b.stockout_pct
    ).slice(0, 10),
    [soDistSort, distData]
  );
  const soDistChartData = {
    labels: sortedSoDist.map(d => d.n),
    datasets: [{
      label: "Stockout %",
      data: sortedSoDist.map(d => parseFloat(d.stockout_pct)),
      backgroundColor: sortedSoDist.map(d =>
        d.stockout_pct > 30 ? "#f87171" : d.stockout_pct > 15 ? "#fdba74" : "#fbbf24"
      ),
      borderRadius: 4, barThickness: 10,
    }],
  };

  const sortedFacType = useMemo(
    () => [...facTypeData].sort((a, b) =>
      soFacType === "desc" ? b.stockout - a.stockout : a.stockout - b.stockout
    ),
    [soFacType, facTypeData]
  );
  const soFacTypeSto = {
    labels: sortedFacType.map(f => f.type),
    datasets: [{
      label: "Stockout %",
      data: sortedFacType.map(f => parseFloat(f.stockout)),
      backgroundColor: sortedFacType.map(f =>
        f.stockout > 30 ? "#dc2626" : f.stockout > 20 ? "#f97316" : f.stockout > 10 ? "#f59e0b" : "#16a34a"
      ),
      borderRadius: 4, barThickness: 14,
    }],
  };

  const sortedSoFac = useMemo(
    () => [...facData].sort((a, b) =>
      soFacSort === "desc" ? b.stockout - a.stockout : a.stockout - b.stockout
    ),
    [soFacSort, facData]
  );
  const soFacChartData = {
    labels: sortedSoFac.map(f => f.n),
    datasets: [{
      label: "Stockout %",
      data: sortedSoFac.map(f => parseFloat(f.stockout)),
      backgroundColor: sortedSoFac.map(f =>
        f.stockout > 40 ? "#f87171" : f.stockout > 30 ? "#fdba74" : "#fbbf24"
      ),
      borderRadius: 4, barThickness: 10,
    }],
  };

  const sortedSoCat = useMemo(
    () => [...catData].sort((a, b) =>
      soCatSort === "desc" ? b.stockout - a.stockout : a.stockout - b.stockout
    ),
    [soCatSort, catData]
  );
  const soCatChartData = {
    labels: sortedSoCat.map(c => c.cat),
    datasets: [{
      label: "Stockout %",
      data: sortedSoCat.map(c => parseFloat(c.stockout)),
      backgroundColor: sortedSoCat.map(c =>
        c.stockout > 30 ? "#f87171" : c.stockout > 20 ? "#fdba74" : c.stockout > 10 ? "#fbbf24" : "#34d399"
      ),
      borderRadius: 4, barThickness: 10,
    }],
  };

  const sortedProducts = useMemo(
    () => [...prodData].sort((a, b) =>
      prodSort === "desc" ? a.months_stock - b.months_stock : b.months_stock - a.months_stock
    ),
    [prodSort, prodData]
  );
  const maxMonths = Math.max(...(prodData.length ? prodData.map(p => p.months_stock) : [1]));

  const options = {
    indexAxis: "y", responsive: true, maintainAspectRatio: false,
    plugins: {
      legend: { display: true, position: "bottom", labels: { boxWidth: 14, font: { size: 11 }, color: "#f3f3f3" } },
      datalabels: { font: { size: 11, weight: 700 }, color: "#f3f3f3" },
    },
    scales: {
      x: { ticks: { callback: v => v + "%" }, grid: { color: "#f1f5f9" } },
      y: { ticks: { font: { size: 10 } } },
    },
  };

  return (
    <div className={`viz-pane active`}>
      <div className="chart-grid-2" style={{ alignItems: "stretch" }}>
        <div className="card" style={{ display: "flex", flexDirection: "column" }}>
          <CardHead icon="fas fa-map-marked-alt" title="Stockout Heat Map" subtitle="Districts by stockout rate" />
          <div className="card-body" style={{ flex: 1, padding: 10 }}>
            <AssamMap mode="stockout" />
          </div>
        </div>
        <div className="card" style={{ display: "flex", flexDirection: "column" }}>
          <CardHead icon="fas fa-exclamation-triangle" title="Facility Type Wise Stockout"
            subtitle={<>
              By type {sortBtn(soFacType === "desc", () => setSoFacType("desc"), "\u25BC")}
              {sortBtn(soFacType === "asc", () => setSoFacType("asc"), "\u25B2")}
            </>}
          />
          <div className="card-body" style={{ flex: 1 }}>
            {facTypeData.length > 0 ? <Bar data={soFacTypeSto} options={options} /> : <p style={{color:'#999'}}>Loading...</p>}
          </div>
        </div>
      </div>
      <div className="chart-grid-3" style={{ marginTop: 14, alignItems: "stretch" }}>
        <div className="card" style={{ display: "flex", flexDirection: "column" }}>
          <CardHead icon="fas fa-chart-bar" title="Top Districts Contributing to Stockout"
            subtitle={<>
              Top 10 {sortBtn(soDistSort === "desc", () => setSoDistSort("desc"), "\u25BC")}
              {sortBtn(soDistSort === "asc", () => setSoDistSort("asc"), "\u25B2")}
            </>}
          />
          <div className="card-body" style={{ flex: 1, minHeight: 280 }}>
            {distData.length > 0 ? <Bar data={soDistChartData} options={options} /> : <p style={{color:'#999'}}>Loading...</p>}
          </div>
        </div>
        <div className="card" style={{ display: "flex", flexDirection: "column" }}>
          <CardHead icon="fas fa-hospital" title="Top Facilities with Low Stock"
            subtitle={<>
              Top 10 {sortBtn(soFacSort === "desc", () => setSoFacSort("desc"), "\u25BC")}
              {sortBtn(soFacSort === "asc", () => setSoFacSort("asc"), "\u25B2")}
            </>}
          />
          <div className="card-body" style={{ flex: 1, minHeight: 280 }}>
            {facData.length > 0 ? <Bar data={soFacChartData} options={options} /> : <p style={{color:'#999'}}>Loading...</p>}
          </div>
        </div>
        <div className="card" style={{ display: "flex", flexDirection: "column" }}>
          <CardHead icon="fas fa-exclamation-triangle" title="Critical SKUs (Low Stock)"
            subtitle={<>
              Top 10 {sortBtn(prodSort === "desc", () => setProdSort("desc"), "\u25BC")}
              {sortBtn(prodSort === "asc", () => setProdSort("asc"), "\u25B2")}
            </>}
          />
          <div className="card-body" style={{ flex: 1 }}>
            <div className="ranked-list">
              {sortedProducts.map((p, i) => (
                <div key={i} className="rank-row">
                  <div className={`rank-num${i < 3 ? " top3" : ""}`}>{i + 1}</div>
                  <div className="rank-name">
                    {p.name}{" "}
                    <span style={{ fontSize: ".6rem", color: "var(--muted)" }}>({p.cat})</span>
                  </div>
                  <div className="rank-bar-wrap">
                    <div className="rank-bar" style={{
                      width: maxMonths > 0 ? (p.months_stock / maxMonths) * 100 + "%" : "0%",
                      background: p.months_stock < 1 ? '#e74c3c' : '#f39c12'
                    }}></div>
                  </div>
                  <div className="rank-val">{p.months_stock} mo</div>
                </div>
              ))}
              {sortedProducts.length === 0 && <p style={{color:'#999', textAlign:'center'}}>Loading...</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
